import { getMailContext } from '../utils/getMailContext';
import { type Suggestions } from '../utils/suggestionCatalog';
import { getEmptyReadingPaneSuggestions } from './getEmptyReadingPaneSuggestions';
import { getSelectedReadingPaneSuggestions } from './getSelectedReadingPaneSuggestions';
import { getComposingSuggestions } from './getComposingSuggestions';
import { isUserStarterOrMSA } from './isUserStarterOrMSA';
import type { ComposeViewState } from 'owa-mail-compose-store';
import type { MailboxInfo } from 'owa-client-types';

/**
 * Some callers (e.g. popouts) know their context and can pass it in directly,
 * while others need to call getMailContext() to determine it, which
 * is specifically used for main window scenarios.
 */
export type MailSuggestionContext = 'reading' | 'composing';

/** Returns the appropriate Copilot suggestions based on the current Mail context */
export const getSuggestions = (
    context: MailSuggestionContext | undefined,
    composeViewState: ComposeViewState | undefined,
    mailboxInfo: MailboxInfo
): Suggestions => {
    const { isComposing, isReading } = context
        ? { isComposing: context === 'composing', isReading: context === 'reading' }
        : getMailContext();

    const emptyMailTabScenario = !isComposing && !isReading; // No email selected

    // Empty Reading Pane Scenario (when no mail is selected)
    // Note that users that are Starter or MSA do not see these SUAs, per design.
    if (emptyMailTabScenario && !isUserStarterOrMSA(mailboxInfo)) {
        return getEmptyReadingPaneSuggestions(mailboxInfo);
    }

    // Selected Reading Pane Scenario (when reading an email)
    if (isReading) {
        return getSelectedReadingPaneSuggestions();
    }

    // Composing Scenario (when composing an email)
    if (isComposing) {
        return getComposingSuggestions(composeViewState);
    }

    return undefined;
};
